import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { SoundOutlined, SafetyOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';
import { useMoopShell } from './MoopShellContext';
import { ReactComponent as ShopBackWordmark } from './shopback-wordmark.svg';
import styles from './MoopSider.module.scss';

type AntdIconType = React.ComponentType<Partial<AntdIconProps>>;

interface NavItem {
  key: string;
  icon: AntdIconType;
  label: string;
  route: string;
}

// Label casing per NAVIGATION.md: sentence case, single capital on first word.
const NAV_ITEMS: NavItem[] = [
  { key: 'catalog', icon: SoundOutlined, label: 'Campaign manager', route: '/campaigns' },
  { key: 'admin', icon: SafetyOutlined, label: 'Admin', route: '/admin' },
];

const MoopSider: React.FC = () => {
  const { sidebarCollapsed, toggleSidebar } = useMoopShell();
  const location = useLocation();
  const history = useHistory();

  const CAMPAIGN_ROUTES = ['/campaigns', '/create/upsize', '/create/standalone-offer', '/create/ad-sales'];
  const isActive = (route: string) => {
    if (route === '/campaigns') {
      return CAMPAIGN_ROUTES.some((r) => location.pathname.startsWith(r));
    }
    return location.pathname.startsWith(route);
  };

  return (
    <aside className={`${styles.sider} ${sidebarCollapsed ? styles.collapsed : ''}`}>
      <div
        className={styles.logo}
        role="button"
        tabIndex={0}
        onClick={() => history.push('/campaigns')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') history.push('/campaigns');
        }}
      >
        {sidebarCollapsed ? (
          <span className={styles.logoCollapsed}>
            {/* Lightning bolt icon from the full wordmark, shown when collapsed */}
            <svg width="20" height="24" viewBox="0 0 24 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M229.906 23.8067H229.136L230.506 22.6774C231.456 21.8947 231.871 20.6037 231.476 19.436C231.081 18.2682 229.972 17.4418 228.702 17.4418H216.552C216.324 17.4418 216.211 17.1646 216.372 17.0055L224.436 9.07765C224.536 8.98012 224.698 9.09049 224.642 9.21881L221.834 15.7864H225.435L232.179 0H228.946L212.944 15.7351C212.061 16.6026 211.817 17.9192 212.341 19.0536C212.828 20.1084 213.919 20.7526 215.079 20.7526H226.928C227.167 20.7526 227.275 21.0528 227.09 21.2043L224.336 23.4756C223.928 23.8118 223.605 24.243 223.397 24.7281L222.375 27.1175H228.204C228.443 27.1175 228.55 27.4177 228.366 27.5692L216.478 37.3603C216.375 37.445 216.226 37.3346 216.28 37.2114L220.592 27.1175L220.959 26.2551L222.604 22.4054H219.003L210.082 43.2837H214.486L231.838 28.9936C232.818 28.1851 233.168 26.8813 232.713 25.6879C232.274 24.5356 231.135 23.8041 229.903 23.8041L229.906 23.8067Z"
                fill="currentColor"
                transform="translate(-210, 0)"
              />
            </svg>
          </span>
        ) : (
          <span className={styles.logoFull}>
            <ShopBackWordmark className={styles.logoWordmark} />
          </span>
        )}
      </div>

      <nav className={styles.nav}>
        {NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            className={`${styles.navItem} ${isActive(item.route) ? styles.active : ''}`}
            onClick={() => history.push(item.route)}
            title={sidebarCollapsed ? item.label : undefined}
          >
            <item.icon style={{ fontSize: 18 }} />
            {!sidebarCollapsed && <span className={styles.navLabel}>{item.label}</span>}
          </button>
        ))}
      </nav>

      <button className={styles.collapseToggle} onClick={toggleSidebar}>
        {sidebarCollapsed ? <RightOutlined style={{ fontSize: 16 }} /> : <LeftOutlined style={{ fontSize: 16 }} />}
        {!sidebarCollapsed && <span className={styles.navLabel}>Collapse</span>}
      </button>
    </aside>
  );
};

export default MoopSider;
